import { SubjectRoles } from "@/api-schema-enums";
import { useAbility } from "@/casl-provider";
import {
  createNavigationLookups,
  filterRoutes,
  filterSidebarItems,
  NavigationLookups,
} from "@/common/helpers/navigation";
import { filterRouteItemsByAudience } from "@/common/helpers/route";
import { sortAppsByOrder } from "@/common/helpers/sort-apps-by-order";
import { useAppFeatureFlag } from "@/common/hooks/use-app-feature-flag";
import { FeatureFlag } from "@/config/feature-flags";
import { navigationConfig } from "@/config/navigation/navigation.config";
import { NavigationItem } from "@/config/navigation/types";
import { userConfigStore } from "@/store/user-config-store";
import { userStore } from "@/store/user-store";
import { useMemo } from "react";

/**
 * Hook to get the filtered navigation configuration based on user permissions and feature flags.
 * Filtered for sidebar display (removes items with showInSidebar: false).
 * @returns The filtered navigation configuration.
 */
export function useFilteredNavigation(): NavigationItem[] {
  const { routeConfig } = useRouteConfig();
  return useMemo(() => filterSidebarItems(routeConfig), [routeConfig]);
}

/**
 * Hook to get the route configuration based on user permissions and feature flags.
 * Includes hidden routes that are not displayed in the sidebar.
 * @returns The route configuration and a loading flag that is true while feature flags are resolving.
 */
export function useRouteConfig(): { routeConfig: NavigationItem[]; isLoading: boolean } {
  const ability = useAbility();
  const user = userStore((state) => state.user);
  const { value: isFitEnabled, loading: isFitLoading } = useAppFeatureFlag(FeatureFlag.FitSidebar);
  const { value: isVehicleBuyInEnabled, loading: isVehicleBuyInLoading } = useAppFeatureFlag(
    FeatureFlag.IsVehicleBuyInEnabled,
  );
  const { value: isNotificationEnabled, loading: isNotificationLoading } = useAppFeatureFlag(FeatureFlag.Notification);
  const isInternalUser = user?.roles.includes(SubjectRoles.Int) ?? false;

  const featureFlags = useMemo(
    () => ({
      [FeatureFlag.FitSidebar]: isFitEnabled,
      [FeatureFlag.IsVehicleBuyInEnabled]: isVehicleBuyInEnabled,
      [FeatureFlag.Notification]: isNotificationEnabled,
    }),
    [isFitEnabled, isVehicleBuyInEnabled, isNotificationEnabled],
  );

  const routeConfig = useMemo(() => {
    const filteredRoutes = filterRoutes(navigationConfig, ability, featureFlags, user?.roles);
    return filterRouteItemsByAudience(filteredRoutes, isInternalUser);
  }, [ability, featureFlags, isInternalUser, user?.roles]);

  return { routeConfig, isLoading: isFitLoading || isVehicleBuyInLoading || isNotificationLoading };
}

export function useSidebarNavigation(): { items: NavigationItem[]; isLoading: boolean } {
  const { routeConfig, isLoading } = useRouteConfig();
  const appOrder = userConfigStore((state) => state.appOrder);

  const items = useMemo(() => {
    const filteredNavigation = filterSidebarItems(routeConfig);
    const pinnedItems = filteredNavigation.filter((item) => item.id === "home");
    const orderedApps = sortAppsByOrder(
      filteredNavigation.filter((item) => item.id !== "home"),
      appOrder,
    );

    return [...pinnedItems, ...orderedApps];
  }, [routeConfig, appOrder]);

  return { items, isLoading };
}

/**
 * Hook to get the navigation lookups (id map, path map, disabled segments set).
 * Optimized for O(1) access.
 */
export function useNavigationLookups(): NavigationLookups {
  const { routeConfig } = useRouteConfig();
  return useMemo(() => createNavigationLookups(routeConfig), [routeConfig]);
}

/**
 * Hook to get a specific navigation item by ID.
 * Checks permissions for the item and all its parents.
 * @param itemId - The ID of the item to retrieve.
 * @returns The navigation item, or undefined if not found or hidden.
 */
export function useNavigationItem(itemId: string): NavigationItem | undefined {
  const lookups = useNavigationLookups();
  return lookups.idMap.get(itemId);
}

/**
 * Hook to get the navigation configuration for a specific module.
 * @param moduleId - The ID of the module to retrieve.
 * @returns The navigation item for the module, or undefined if not found or hidden.
 */
export function useModuleNavigation(moduleId: string): NavigationItem | undefined {
  return useNavigationItem(moduleId);
}
